// src/components/Clinician/CreateSelfReport.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  IconButton,
  Chip,
  Stack,
  Grid
} from '@mui/material';
import {
  Save,
  Close,
  CheckCircle,
  Description,
  Visibility
} from '@mui/icons-material';
import {
  createSelfReport,
  updateSelfReport,
  approveSelfReport,
  getSelfReport,
  autoSaveReport,
  getReportTemplate,
  resolveReportStudyId
} from '../../services/reports';
import { formatPatientName, generateOHIFUrl } from '../../services/utils';
import api from '../../services/api';
import ReportTemplateForm from '../Common/ReportTemplateForm';
import { useThemeMode } from '../../contexts/ThemeModeContext';

const CreateSelfReport = ({ open, onClose, study, existingReport, onSuccess }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#475569';
  const cardBorder = isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)';

  const studyApiId = study ? resolveReportStudyId(study) : null;
  const [activeStep, setActiveStep] = useState(0);
  const [reportData, setReportData] = useState({
    clinicalHistory: '',
    technique: '',
    comparison: '',
    findings: '',
    impression: '',
    recommendations: '',
    modalityFields: {}
  });
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [reportId, setReportId] = useState(existingReport?._id || null);
  const [reportStatus, setReportStatus] = useState(existingReport?.status || null);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);
  const [viewerUrl, setViewerUrl] = useState('');
  const [viewerError, setViewerError] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);

  const steps = ['Patient & Study Info', 'Report Content', 'Review & Approve'];

  // Check if study is claimed by a radiologist
  const assignedId = (study?.assignedRadiologist?._id || study?.assignedRadiologist)?.toString();
  const isClaimedByRadiologist = Boolean(
    (['claimed', 'in_progress'].includes(study?.dycovueReportStatus) || assignedId || study?.orderStatus === 'claimed') &&
    study?.dycovueReportStatus !== 'completed' &&
    study?.selfReportStatus !== 'completed'
  );

  // Load existing report and template
  useEffect(() => {
    const loadData = async () => {
      if (open) {
        if (isClaimedByRadiologist) {
          setError('This study has been claimed by a radiologist. Self-reporting is locked.');
          setLoading(false);
          return;
        }
        setLoading(true);
        setError('');
        
        // Validate study object
        if (!study || !studyApiId) {
          setError('Invalid study data. Please refresh and try again.');
          setLoading(false);
          return;
        }
        
        console.log('CreateSelfReport: Loading data for study ID:', studyApiId);
        
        try {
          // Load template
          if (study?.modality) {
            const templateData = await getReportTemplate(study.modality);
            setTemplate(templateData);
          }

          // Load viewer URL for image access - use same approach as clinician dashboard
          // OHIF viewer URL format: https://ohif-viewer.dycovue.com/viewer/{StudyInstanceUID}
          const ohifBaseUrl = process.env.REACT_APP_OHIF_URL || 'https://ohif-viewer.dycovue.com';
          const orthancBaseUrl = process.env.REACT_APP_ORTHANC_URL || 'https://orthanc.dycovue.com';
          
          // Debug: Log study object to help troubleshoot
          console.log('CreateSelfReport: Study object:', study);
          console.log('CreateSelfReport: studyInstanceUID:', study?.studyInstanceUID);
          console.log('CreateSelfReport: orthancStudyId:', study?.orthancStudyId);
          
          if (study?.studyInstanceUID) {
            // Correct OHIF URL format: /viewer/{studyInstanceUID}
            const viewerUrlStr = generateOHIFUrl(study.studyInstanceUID, ohifBaseUrl);
            setViewerUrl(viewerUrlStr);
            console.log('OHIF viewer URL:', viewerUrlStr);
          } else if (study?.orthancStudyId) {
            // Fallback: resolve studyInstanceUID through the authenticated backend (never call Orthanc directly)
            try {
              const viewerResp = await api.get(`/studies/${study._id || study.orthancStudyId}/viewer-url`);
              const studyUID = viewerResp?.data?.studyInstanceUID;
              if (studyUID) {
                // Correct OHIF URL format: /viewer/{studyInstanceUID}
                const viewerUrlStr = generateOHIFUrl(studyUID, ohifBaseUrl);
                setViewerUrl(viewerUrlStr);
                console.log('OHIF viewer URL (from Orthanc):', viewerUrlStr);
              } else {
                console.warn('Study UID not found in Orthanc response');
                setViewerError('Study UID not found in Orthanc');
              }
            } catch (err) {
              console.error('Error fetching study UID from Orthanc:', err);
              setViewerError('Could not fetch study details from Orthanc');
            }
          } else {
            console.warn('No studyInstanceUID or orthancStudyId available for study:', study?._id);
            setViewerError('Study UID not available - the viewer may not work');
          }

          // Try to load existing report (from prop or by fetching from server)
          // CHANGED: Load ANY existing report (draft, approved, etc.) so form can be populated and PDF shown
          let reportToLoad = existingReport;
          if (!reportToLoad && studyApiId) {
            try {
              const existing = await getSelfReport(studyApiId);
              if (existing) {
                reportToLoad = existing;
              }
            } catch (err) {
              // Check the error message to distinguish between "study not found" vs "report not found"
              const errorMsg = err.response?.data?.error || '';
              if (err.response?.status === 404) {
                if (errorMsg.includes('Study not found')) {
                  // This is a real error - the study doesn't exist
                  console.error('Study not found:', studyApiId);
                  setError('Study not found. Please refresh and try again.');
                } else {
                  // "Report not found" is expected when creating a new report
                  console.log('No existing report found for this study');
                }
              } else if (err.response?.status === 403) {
                // Access denied - log but don't show error since user might not have permission
                console.warn('Access denied to existing report:', errorMsg);
                // Don't set error - allow user to create new report
              } else {
                console.error('Error fetching existing report:', errorMsg || err.message);
                // Don't show error for 403 - allow user to continue
                if (err.response?.status !== 403) {
                  setError(`Warning: Could not load existing report - ${errorMsg}`);
                }
              }
            }
          }

          if (reportToLoad) {
            setReportData({
              clinicalHistory: reportToLoad.clinicalHistory || '',
              technique: reportToLoad.technique || '',
              comparison: reportToLoad.comparison || '',
              findings: reportToLoad.findings || '',
              impression: reportToLoad.impression || '',
              recommendations: reportToLoad.recommendations || '',
              modalityFields: reportToLoad.modalityFields || {}
            });
            setReportId(reportToLoad._id);
            setReportStatus(reportToLoad.status || 'draft');
            
            // If report is approved, fetch PDF as blob and create object URL for viewing
            // Also navigate to review step to show the PDF
            if (reportToLoad.status === 'approved' && reportToLoad._id) {
              try {
                const response = await api.get(`/reports/${reportToLoad._id}/pdf`, {
                  responseType: 'blob'
                });
                const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                setPdfUrl(blobUrl);
                // Navigate to review step (step 2) to show the approved report and PDF
                setActiveStep(2);
              } catch (pdfErr) {
                console.error('Error loading PDF:', pdfErr);
                // PDF might not be generated yet - that's okay, user can still view the report content
                // The PDF endpoint will generate it on-the-fly when accessed
                console.log('PDF may not be pre-generated, will be generated on access');
              }
            }
          } else {
            // Reset form data when creating new report
            setReportData({
              clinicalHistory: '',
              technique: '',
              comparison: '',
              findings: '',
              impression: '',
              recommendations: '',
              modalityFields: {}
            });
            setReportId(null);
            setReportStatus(null);
            setPdfUrl(null);
          }
        } catch (err) {
          console.error('Load data error:', err);
          setError(' fAIL to load report template');
        } finally {
          setLoading(false);
        }
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, studyApiId, existingReport]);

  // Auto-save functionality
  const triggerAutoSave = useCallback(() => {
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }

    const timeout = setTimeout(async () => {
      // Only auto-save if we have at least some data entered
      const hasData = reportData.clinicalHistory || 
                     reportData.technique || 
                     reportData.findings || 
                     reportData.impression;
      
      if (!hasData) {
        return; // Don't auto-save empty forms
      }

      try {
        if (reportId) {
          // Auto-save existing report
          try {
            await autoSaveReport(reportId, reportData);
            console.log('Auto-saved at', new Date().toLocaleTimeString());
          } catch (autoSaveErr) {
            // Handle errors gracefully - don't show to user for auto-save failures
            if (autoSaveErr.response?.status === 400) {
              const errorMsg = autoSaveErr.response?.data?.error || '';
              if (errorMsg.includes('Can only auto-save drafts')) {
                console.log('Auto-save skipped: report is no longer in draft status');
                // Report status changed, stop auto-saving
                return;
              }
            }
            // For other errors, just log them
            console.error('Auto-save failed:', autoSaveErr.response?.data?.error || autoSaveErr.message);
          }
        } else if (studyApiId) {
          try {
            const existing = await getSelfReport(studyApiId);
            if (existing && existing.status === 'draft') {
              setReportId(existing._id);
              try {
                await autoSaveReport(existing._id, reportData);
                console.log('Auto-saved existing draft at', new Date().toLocaleTimeString());
              } catch (autoSaveErr) {
                // Handle auto-save errors gracefully
                if (autoSaveErr.response?.status === 400) {
                  console.log('Auto-save skipped: report status changed');
                } else {
                  console.error('Auto-save failed:', autoSaveErr.response?.data?.error || autoSaveErr.message);
                }
              }
            }
          } catch (fetchErr) {
            // No existing report, but we have data - user will need to click Save Draft
            // to create the initial report (since backend requires all fields for creation)
            if (fetchErr.response?.status === 404) {
              console.log('Auto-save skipped: no existing draft report. Please click "Save Draft" to create one.');
            } else {
              console.error('Error checking for existing report:', fetchErr.response?.data?.error || fetchErr.message);
            }
          }
        }
      } catch (err) {
        // Catch any unexpected errors
        console.error('Auto-save failed:', err.response?.data?.error || err.message);
      }
    }, 3000); // Auto-save after 3 seconds of inactivity

    setAutoSaveTimeout(timeout);
  }, [reportId, reportData, autoSaveTimeout, studyApiId]);

  // Trigger auto-save when report data changes (only for draft reports)
  useEffect(() => {
    if (reportId && reportStatus === 'draft') {
      triggerAutoSave();
    }
    return () => {
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounced autosave manages its own timeout
  }, [reportData, reportId, reportStatus]);

  const handleFieldChange = (field, value) => {
    setReportData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleModalityFieldChange = (fieldName, value) => {
    setReportData(prev => ({
      ...prev,
      modalityFields: {
        ...prev.modalityFields,
        [fieldName]: value
      }
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 0:
        return true; // Patient info is read-only
      case 1:
        if (!reportData.clinicalHistory?.trim()) {
          setError('Clinical history is required');
          return false;
        }
        if (!reportData.technique?.trim()) {
          setError('Technique is required');
          return false;
        }
        if (!reportData.findings?.trim()) {
          setError('Findings are required');
          return false;
        }
        if (!reportData.impression?.trim()) {
          setError('Impression is required');
          return false;
        }
        return true;
      case 2:
        return true; // Review step
      default:
        return true;
    }
  };

  const handleNext = () => {
    setError('');
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    setError('');

    // Validate study exists before proceeding
    if (!study || !studyApiId) {
      setError('Study data is missing. Please refresh and try again.');
      setSaving(false);
      return;
    }

    try {
      let currentReportId = reportId;
      if (!currentReportId) {
        try {
          const existing = await getSelfReport(studyApiId);
          if (existing) {
            currentReportId = existing._id;
            setReportId(existing._id);
            
            // If report is not in draft status, we can't update it
            if (existing.status !== 'draft') {
              setError(`Cannot update report: Report is ${existing.status}. Please create a new report or contact an administrator.`);
              setSaving(false);
              return;
            }
          }
        } catch (fetchErr) {
          // No existing report found, that's okay - we'll create one
          if (fetchErr.response?.status !== 404) {
            console.error('Error checking for existing report:', fetchErr);
          }
        }
      }

      if (currentReportId) {
        // Update existing report using updateSelfReport (allows partial updates)
        try {
          await updateSelfReport(currentReportId, reportData);
        } catch (updateErr) {
          // Handle case where report is not in draft status
          if (updateErr.response?.status === 400) {
            const errorMsg = updateErr.response?.data?.error || '';
            if (errorMsg.includes('Can only update draft reports')) {
              setError('Cannot update report: Report is no longer in draft status. Please refresh the page.');
              setSaving(false);
              return;
            }
          }
          throw updateErr;
        }
      } else {
        // Create new draft report - backend requires all fields, so provide defaults if missing
        const draftData = {
          clinicalHistory: reportData.clinicalHistory || 'Draft - To be completed',
          technique: reportData.technique || 'Draft - To be completed',
          findings: reportData.findings || 'Draft - To be completed',
          impression: reportData.impression || 'Draft - To be completed',
          comparison: reportData.comparison || '',
          recommendations: reportData.recommendations || '',
          modalityFields: reportData.modalityFields || {}
        };
        
        try {
          const result = await createSelfReport(studyApiId, draftData);
          const newReportId = result._id;
          setReportId(newReportId);
          
          // If we have actual user data (not just defaults), update the report
          const hasRealData = reportData.clinicalHistory || 
                             reportData.technique || 
                             reportData.findings || 
                             reportData.impression;
          
          if (hasRealData) {
            // Update with actual user data
            await updateSelfReport(newReportId, reportData);
          }
        } catch (createErr) {
          // Handle conflict - report already exists
          if (createErr.response?.status === 409) {
            // Report already exists, use the reportId from error response and update it
            const existingReportId = createErr.response?.data?.reportId;
            if (existingReportId) {
              setReportId(existingReportId);
              // Check if it's in draft status before updating
              try {
                const existing = await getSelfReport(studyApiId);
                if (existing && existing.status === 'draft') {
                  await updateSelfReport(existingReportId, reportData);
                } else {
                  setError(`Cannot update report: Report is ${existing?.status || 'not in draft status'}. Please refresh the page.`);
                  setSaving(false);
                  return;
                }
              } catch (updateErr) {
                throw updateErr;
              }
            } else {
              throw new Error('Report already exists but reportId not provided');
            }
          } else {
            // Re-throw other errors (like study not found, validation errors, etc.)
            throw createErr;
          }
        }
      }

      onSuccess?.();
    } catch (err) {
      console.error('Save draft error:', err);
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.errors?.[0]?.msg || 
                          err.message ||
                          'Failed to save draft';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!validateStep(1)) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      let finalReportId = reportId;

      // Create or update report first
      if (!reportId) {
        try {
          const result = await createSelfReport(studyApiId, reportData);
          finalReportId = result._id;
          setReportId(finalReportId);
        } catch (createErr) {
          // Handle conflict - report already exists
          if (createErr.response?.status === 409) {
            // Report already exists, use the reportId from error response
            const existingReportId = createErr.response?.data?.reportId;
            if (existingReportId) {
              finalReportId = existingReportId;
              setReportId(finalReportId);
              // Try to update the existing report before approving
              try {
                const existing = await getSelfReport(studyApiId);
                if (existing && existing.status === 'draft') {
                  await updateSelfReport(existingReportId, reportData);
                } else {
                  console.log('Report not in draft status, proceeding to approval with existing content');
                }
              } catch (updateErr) {
                // If update fails, still proceed to approval
                console.log('Could not update existing report, proceeding to approval');
              }
            } else {
              throw new Error('Report already exists but reportId not provided');
            }
          } else {
            // Re-throw other errors (validation errors, etc.)
            throw createErr;
          }
        }
      } else {
        // Try to update, but if report is not in draft status, skip update and go straight to approval
        try {
          await updateSelfReport(reportId, reportData);
        } catch (updateErr) {
          // If update fails because report is not in draft, that's okay - we'll approve with existing content
          const errorMsg = updateErr.response?.data?.error || '';
          if (updateErr.response?.status === 400 && errorMsg.includes('Can only update draft reports')) {
            console.log('Report not in draft status, skipping update and proceeding to approval');
          } else {
            // Re-throw other errors (validation errors, etc.)
            throw updateErr;
          }
        }
      }

      // Then approve
      await approveSelfReport(finalReportId);

      onSuccess?.();
      onClose();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || err.message || 'Failed to approve report';
      setError(errorMsg);
      console.error('Approve error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      // Clean up PDF blob URL if it exists
      if (pdfUrl && pdfUrl.startsWith('blob:')) {
        window.URL.revokeObjectURL(pdfUrl);
      }
      setActiveStep(0);
      setReportData({
        clinicalHistory: '',
        technique: '',
        comparison: '',
        findings: '',
        impression: '',
        recommendations: '',
        modalityFields: {}
      });
      setError('');
      setReportId(null);
      setReportStatus(null);
      setPdfUrl(null);
      onClose();
    }
  };

  const handleOpenViewer = () => {
    // If we have a viewerUrl, use it directly
    if (viewerUrl) {
      window.open(viewerUrl, '_blank');
      return;
    }
    
    // Fallback: construct viewer URL from study data
    // Correct OHIF URL format: /viewer/{studyInstanceUID}
    const ohifBaseUrl = process.env.REACT_APP_OHIF_URL || 'https://ohif-viewer.dycovue.com';
    if (study?.studyInstanceUID) {
      const url = generateOHIFUrl(study.studyInstanceUID, ohifBaseUrl);
      console.log('Opening OHIF viewer (fallback):', url);
      window.open(url, '_blank');
    } else {
      console.error('Cannot open viewer: No studyInstanceUID available');
      setError('Cannot open viewer: Study UID not available');
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ color: textColorPrimary, mb: 0.5 }}>
              Patient & Study Information
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
              Review the patient and study details below. This information is automatically populated from the study.
            </Typography>

            {viewerError && (
              <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
                {viewerError}
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 2, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#F8FAFC', borderRadius: '8px', border: cardBorder }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 1.25, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF', borderRadius: '6px', border: cardBorder }}>
                    <Typography variant="caption" sx={{ color: textColorSecondary, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase' }}>
                      Patient Name
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: textColorPrimary, mt: 0.25 }}>
                      {formatPatientName(study?.patientName)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 1.25, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF', borderRadius: '6px', border: cardBorder }}>
                    <Typography variant="caption" sx={{ color: textColorSecondary, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase' }}>
                      Patient ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: textColorPrimary, mt: 0.25 }}>
                      {study?.patientId || 'Unknown'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 1.25, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF', borderRadius: '6px', border: cardBorder }}>
                    <Typography variant="caption" sx={{ color: textColorSecondary, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase' }}>
                      Study Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: textColorPrimary, mt: 0.25 }}>
                      {study?.studyDate ? new Date(study.studyDate).toLocaleDateString() : 'Unknown'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 1.25, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF', borderRadius: '6px', border: cardBorder }}>
                    <Typography variant="caption" sx={{ color: textColorSecondary, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase' }}>
                      Modality
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0D9488', mt: 0.25 }}>
                      {study?.modality || 'Unknown'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 1.25, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF', borderRadius: '6px', border: cardBorder }}>
                    <Typography variant="caption" sx={{ color: textColorSecondary, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase' }}>
                      Accession Number
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: textColorPrimary, mt: 0.25 }}>
                      {study?.accessionNumber || '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 1.25, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF', borderRadius: '6px', border: cardBorder }}>
                    <Typography variant="caption" sx={{ color: textColorSecondary, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase' }}>
                      Study Description
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: textColorPrimary, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {study?.studyDescription || 'No description'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<Visibility sx={{ fontSize: 18 }} />}
              onClick={handleOpenViewer}
              disabled={!viewerUrl && !study?.studyInstanceUID && !study?.orthancStudyId}
              sx={{ mt: 2, height: 36, textTransform: 'none', borderRadius: '8px', fontSize: '0.85rem' }}
            >
              {viewerUrl ? 'Open Image Viewer in New Tab (OHIF)' : 'Loading Viewer...'}
            </Button>
          </Box>
        );

      case 1:
        return (
          <Box>
            {error && (
              <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              {/* Embedded Image Viewer */}
              <Box
                sx={{
                  flexBasis: { md: '45%' },
                  flexShrink: 0,
                  height: { xs: 260, md: 410 },
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.default',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#F8FAFC'
                  }}
                >
                  <Typography variant="caption" fontWeight={700} sx={{ color: textColorPrimary }}>
                    OHIF Viewer
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Visibility sx={{ fontSize: 14 }} />}
                    onClick={handleOpenViewer}
                    disabled={!viewerUrl && !study?.studyInstanceUID}
                    sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.25 }}
                  >
                    Open New Tab
                  </Button>
                </Box>
                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                  {viewerUrl ? (
                    <iframe
                      src={viewerUrl}
                      title="OHIF Viewer"
                      style={{ width: '100%', height: '100%', border: 'none' }}
                      allow="fullscreen"
                    />
                  ) : (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%',
                        flexDirection: 'column',
                        gap: 1.5,
                        p: 2
                      }}
                    >
                      {viewerError ? (
                        <>
                          <Alert severity="warning" sx={{ maxWidth: 320, py: 0.5, fontSize: '0.75rem' }}>
                            {viewerError}
                          </Alert>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Visibility />}
                            onClick={handleOpenViewer}
                            disabled={!study?.studyInstanceUID && !study?.orthancStudyId}
                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                          >
                            Try Opening Viewer
                          </Button>
                        </>
                      ) : (
                        <CircularProgress size={28} />
                      )}
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Report Form */}
              <Box sx={{ flexBasis: { md: '55%' }, flexGrow: 1 }}>
                {reportStatus === 'approved' && (
                  <Alert severity="info" sx={{ mb: 1, py: 0.25 }}>
                    This report has been approved and is read-only.
                  </Alert>
                )}
                
                <Grid container spacing={1}>
                  {/* Clinical History */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      rows={2}
                      size="small"
                      label="Clinical History"
                      placeholder="Symptoms, history..."
                      value={reportData.clinicalHistory}
                      onChange={(e) => handleFieldChange('clinicalHistory', e.target.value)}
                      disabled={reportStatus === 'approved'}
                    />
                  </Grid>

                  {/* Technique */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      rows={2}
                      size="small"
                      label="Technique"
                      placeholder="Protocol, sequences..."
                      value={reportData.technique}
                      onChange={(e) => handleFieldChange('technique', e.target.value)}
                      disabled={reportStatus === 'approved'}
                    />
                  </Grid>

                  {/* Comparison */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={1}
                      size="small"
                      label="Comparison"
                      placeholder="Prior studies..."
                      value={reportData.comparison}
                      onChange={(e) => handleFieldChange('comparison', e.target.value)}
                      disabled={reportStatus === 'approved'}
                    />
                  </Grid>

                  {/* Recommendations */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={1}
                      size="small"
                      label="Recommendations"
                      placeholder="Follow-up imaging..."
                      value={reportData.recommendations}
                      onChange={(e) => handleFieldChange('recommendations', e.target.value)}
                      disabled={reportStatus === 'approved'}
                    />
                  </Grid>

                  {/* Findings */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      rows={3}
                      size="small"
                      label="Findings"
                      placeholder="Detailed description of imaging findings..."
                      value={reportData.findings}
                      onChange={(e) => handleFieldChange('findings', e.target.value)}
                      disabled={reportStatus === 'approved'}
                    />
                  </Grid>

                  {/* Impression */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      rows={2}
                      size="small"
                      label="Impression / Conclusion"
                      placeholder="Summary of key findings..."
                      value={reportData.impression}
                      onChange={(e) => handleFieldChange('impression', e.target.value)}
                      disabled={reportStatus === 'approved'}
                    />
                  </Grid>
                </Grid>

                {/* Modality-Specific Fields */}
                {template && template.customFields && template.customFields.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" fontWeight={700} gutterBottom sx={{ color: textColorPrimary, display: 'block' }}>
                      {study?.modality} Specific Measurements
                    </Typography>
                    <ReportTemplateForm
                      template={template}
                      values={reportData.modalityFields}
                      onChange={handleModalityFieldChange}
                    />
                  </Box>
                )}
                
                {/* PDF Viewer for Approved Reports */}
                {reportStatus === 'approved' && pdfUrl && (
                  <Box sx={{ mt: 1, border: 1, borderColor: 'divider', borderRadius: '6px', overflow: 'hidden', height: 200 }}>
                    <iframe
                      src={pdfUrl}
                      title="Report PDF"
                      style={{ width: '100%', height: '100%', border: 'none' }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ color: textColorPrimary, mb: 0.5 }}>
              Review & Approve Report
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
              Please review your report carefully before approval. Once approved, the report will be finalized and a PDF will be generated.
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#F8FAFC', borderRadius: '8px', border: cardBorder }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 1, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF', borderRadius: '6px', border: cardBorder, height: '100%' }}>
                    <Typography variant="caption" sx={{ color: '#0D9488', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase' }}>
                      Clinical History
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: textColorPrimary, mt: 0.25, fontSize: '0.8rem' }}>
                      {reportData.clinicalHistory || '-'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 1, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF', borderRadius: '6px', border: cardBorder, height: '100%' }}>
                    <Typography variant="caption" sx={{ color: '#0D9488', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase' }}>
                      Technique
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: textColorPrimary, mt: 0.25, fontSize: '0.8rem' }}>
                      {reportData.technique || '-'}
                    </Typography>
                  </Box>
                </Grid>

                {reportData.comparison && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF', borderRadius: '6px', border: cardBorder, height: '100%' }}>
                      <Typography variant="caption" sx={{ color: '#0D9488', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase' }}>
                        Comparison
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: textColorPrimary, mt: 0.25, fontSize: '0.8rem' }}>
                        {reportData.comparison}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {reportData.recommendations && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF', borderRadius: '6px', border: cardBorder, height: '100%' }}>
                      <Typography variant="caption" sx={{ color: '#0D9488', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase' }}>
                        Recommendations
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: textColorPrimary, mt: 0.25, fontSize: '0.8rem' }}>
                        {reportData.recommendations}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box sx={{ p: 1, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF', borderRadius: '6px', border: cardBorder }}>
                    <Typography variant="caption" sx={{ color: '#0D9488', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase' }}>
                      Findings
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: textColorPrimary, mt: 0.25, fontSize: '0.8rem' }}>
                      {reportData.findings || '-'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ p: 1, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF', borderRadius: '6px', border: cardBorder }}>
                    <Typography variant="caption" sx={{ color: '#0D9488', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase' }}>
                      Impression
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: textColorPrimary, mt: 0.25, fontSize: '0.8rem' }}>
                      {reportData.impression || '-'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* PDF Viewer for Approved Reports */}
            {reportStatus === 'approved' && pdfUrl && (
              <Box sx={{ mt: 1.5, border: 1, borderColor: 'divider', borderRadius: '8px', overflow: 'hidden', height: 350, bgcolor: 'background.default' }}>
                <iframe
                  src={pdfUrl}
                  title="Report PDF"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              </Box>
            )}

            {reportStatus !== 'approved' && (
              <Alert severity="info" sx={{ mt: 1.5, py: 0.5, fontSize: '0.78rem' }}>
                By approving this report, you confirm that all information is accurate and complete. 
                A PDF will be automatically generated and attached to the study.
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      disableEscapeKeyDown={saving}
      PaperProps={{
        sx: {
          width: { xs: '95vw', sm: '92vw', md: '980px', lg: '1040px' },
          maxWidth: '1040px !important',
          maxHeight: '90vh',
          bgcolor: cardBg,
          color: textColorPrimary,
          borderRadius: '12px',
          border: cardBorder,
          boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.5)' : '0 12px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: cardBorder }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Description color="primary" sx={{ fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: textColorPrimary, fontSize: '0.95rem' }}>
              {reportStatus === 'approved' ? 'View' : existingReport ? 'Edit' : 'Create'} Self-Report
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={saving} size="small" sx={{ p: '3px', color: textColorSecondary }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2, py: 1.5 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            <Stepper activeStep={activeStep} sx={{ mb: 2, '& .MuiStepLabel-label': { fontSize: '0.78rem' } }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {renderStepContent(activeStep)}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.25, borderTop: cardBorder, justifyContent: 'space-between' }}>
        <Box>
          {reportId && reportStatus === 'draft' && (
            <Chip 
              label="Auto-saving..." 
              size="small" 
              color="success" 
              variant="outlined"
              sx={{ opacity: 0.7, height: 24, fontSize: '0.7rem' }}
            />
          )}
          {reportStatus === 'approved' && (
            <Chip 
              label="Approved Report" 
              size="small" 
              color="success" 
              variant="filled"
              sx={{ height: 24, fontSize: '0.7rem' }}
            />
          )}
        </Box>

        <Box display="flex" gap={1}>
          <Button onClick={handleClose} disabled={saving} sx={{ height: 30, fontSize: '0.8rem', borderRadius: '6px', textTransform: 'none', px: 1.5 }}>
            Cancel
          </Button>

          {activeStep > 0 && (
            <Button onClick={handleBack} disabled={saving} sx={{ height: 30, fontSize: '0.8rem', borderRadius: '6px', textTransform: 'none', px: 1.5 }}>
              Back
            </Button>
          )}

          {activeStep < steps.length - 1 && reportStatus !== 'approved' && (
            <>
              <Button
                onClick={handleSaveDraft}
                startIcon={saving ? <CircularProgress size={14} /> : <Save sx={{ fontSize: 16 }} />}
                disabled={saving}
                sx={{ height: 30, fontSize: '0.8rem', borderRadius: '6px', textTransform: 'none', px: 1.5 }}
              >
                Save Draft
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={saving}
                sx={{
                  height: 30,
                  fontSize: '0.8rem',
                  borderRadius: '6px',
                  textTransform: 'none',
                  px: 1.75,
                  bgcolor: '#0D9488',
                  '&:hover': { bgcolor: '#0F766E' }
                }}
              >
                Next
              </Button>
            </>
          )}

          {activeStep === steps.length - 1 && reportStatus !== 'approved' && (
            <>
              <Button
                onClick={handleSaveDraft}
                startIcon={saving ? <CircularProgress size={14} /> : <Save sx={{ fontSize: 16 }} />}
                disabled={saving}
                sx={{ height: 30, fontSize: '0.8rem', borderRadius: '6px', textTransform: 'none', px: 1.5 }}
              >
                Save Draft
              </Button>
              <Button
                variant="contained"
                onClick={handleApprove}
                startIcon={saving ? <CircularProgress size={14} /> : <CheckCircle sx={{ fontSize: 16 }} />}
                disabled={saving}
                color="success"
                sx={{
                  height: 30,
                  fontSize: '0.8rem',
                  borderRadius: '6px',
                  textTransform: 'none',
                  px: 1.75
                }}
              >
                Approve & Finalize
              </Button>
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSelfReport;
